import React, { useState, useEffect } from "react";

const BattingStatsTable = ({
  topBatPerformers,
  selectedFilter,
  yearDataLoading = true,
  selectedYear,
}) => {
  const [dataColumns, setDataColumns] = useState([
    { key: "runs_scored", label: "Runs", highlight: "Most Runs" },
    { key: "matches_played", label: "Matches" },
    { key: "innings_played", label: "Inns" },
    { key: "highest_score", label: "HS", highlight: "Best Individual Score" },
    { key: "average", label: "AVG", highlight: "Best Batting Average" },
    { key: "batting_strike_rate", label: "SR", highlight: "Best Strike Rate" },
    { key: "hundred", label: "100s", highlight: "Most 100s" },
    { key: "fifties", label: "50s", highlight: "Most 50s" },
    { key: "sixes", label: "6s", highlight: "Most 6s" },
    { key: "fours", label: "4s", highlight: "Most 4s" },
  ]);

  useEffect(() => {
    let columns = [...dataColumns];
    const selectedIndex = columns.findIndex(
      (item) => item.highlight === selectedFilter
    );
    if (selectedIndex > 0) {
      const [selectedColumn] = columns.splice(selectedIndex, 1);
      columns.unshift(selectedColumn);
    }
    setDataColumns(columns);
  }, [selectedFilter]);

  // Get selected column key for sorting
  const selectedColumn = dataColumns.find(
    (col) => col.highlight === selectedFilter
  );

  return (
    <div className="relative">
      <div className="md:w-full md:overflow-hidden overflow-x-auto">
        <div className="flex py-3 min-w-[800px]">
          <div className="flex w-[35%] text-[#FFFFFF] text-[12px] font-[500]">
            <div className="text-[14px] font-[400] text-[#000000] dark:text-white dark:text-[#FFFFFF80] w-[20%] text-center">
              Pos
            </div>
            <div className="text-[14px] font-[400] text-[#000000] dark:text-white dark:text-[#FFFFFF80] w-[80%]">
              Player Name
            </div>
          </div>
          <div className="w-[65%] text-center">
            <div className="flex items-center justify-around">
              {dataColumns &&
                dataColumns.map((item, index) => (
                  <div
                    key={index}
                    className={`text-[14px] ${selectedFilter == item?.highlight
                      ? "font-bold"
                      : "font-[400]"
                      } text-[#000000] dark:text-white w-[24%]`}
                  >
                    {item?.label}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {yearDataLoading
          ? [...Array(5)].map((_, index) => (
            <div
              className="flex py-4 min-w-[800px] animate-pulse"
              key={index}
            >
              <div className="flex w-[35%]">
                <div className="bg-gray-300 h-6 w-10 rounded-md mx-auto"></div>
                <div className="flex w-[80%] items-center gap-3">
                  <div className="bg-gray-300 h-[50px] w-[50px] rounded-md"></div>
                  <div>
                    <div className="bg-gray-300 h-4 w-24 rounded-md mb-1"></div>
                    <div className="bg-gray-300 h-3 w-16 rounded-md"></div>
                  </div>
                </div>
              </div>
              <div className="w-[65%] text-center">
                <div className="flex items-center justify-around">
                  {[...Array(9)].map((_, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-300 h-6 w-12 rounded-md"
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          ))
          : [...(topBatPerformers ?? [])]
            .sort((a, b) => {
              if (!selectedColumn) return 0;

              const parseValue = (val) => {
                if (val == null) return -Infinity;

                // Remove '*' if present and parse as float
                if (typeof val === "string") {
                  const numeric = parseFloat(val.replace("*", ""));
                  return isNaN(numeric) ? -Infinity : numeric;
                }

                return typeof val === "number" ? val : -Infinity;
              };

              const aVal = parseValue(a[selectedColumn.key]);
              const bVal = parseValue(b[selectedColumn.key]);

              return bVal - aVal;
            })
            .slice(0, 5)

            .map((value, index) => (
              <div
                className={`flex ${index % 2 === 0
                  ? "bg-gray-200 dark:bg-gray-800"
                  : "bg-white dark:bg-[#1F1F1F]"
                  } py-3 min-w-[800px] items-center`}
                key={index}
              >
                <div className="flex w-[35%] text-[#FFFFFF] text-[12px] font-[500] items-center">
                  <div className="text-[14px] font-[400] text-[#525358] dark:text-[#FFFFFF80] w-[20%] text-center">
                    {index + 1}
                  </div>
                  <div className="text-[14px] font-[400] text-[#525358] dark:text-[#FFFFFF80] w-[80%]">
                    <div className="flex">
                      <img
                        width={"50px"}
                        height={"50px"}
                        className="w-[50px] h-[50px] mr-3 rounded-full"
                        src={`https://akm-img-a-in.tosshub.com/sites/sports/ipl/resources_live/images/players_img/ipl_2025/${value?.player_id}.png`}
                        onError={(e) => {
                          e.target.src =
                            "https://akm-img-a-in.tosshub.com/sites/sports/ipl/resources_live/images/players_img/ipl_2025/0.png";
                        }}
                      />
                      <div className="font-semibold text-[#525358] dark:text-[#FFFFFFCC]">
                        {value.player_name}
                        <div className="font-normal">
                          {value?.team_short_name ?? "-"}
                        </div>
                      </div>
                      {selectedFilter === "Most Runs" && index === 0 && (
                        <img
                          className="h-5 ml-2"
                          src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/orange_cap.png"
                          alt="Orange Cap"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-[65%] text-center">
                  <div className="flex items-center justify-around">
                    {dataColumns &&
                      dataColumns.map((item, index) => (
                        <div
                          key={index}
                          className={`text-[14px] ${selectedFilter === item?.highlight
                            ? "font-bold text-[#000] dark:text-white"
                            : "font-[400] text-[#525358] dark:text-[#ffffffcc]"
                            } w-[24%]`}
                        >
                          {value[item?.key] ?? "-"}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))}
      </div>

      <div className="flex justify-center w-full">
        {topBatPerformers && topBatPerformers.length > 0 ? (
          <a
            href={
              selectedYear === "2025"
                ? "/sports/ipl/orange-cap"
                : `/sports/ipl/orange-cap-winner-${selectedYear}`
            }
          >
            <div className="flex items-center gap-2 rounded-full h-10 w-auto px-4 bg-primary mt-4 text-white dark:text-[#1f1f1f] justify-center">
              <img src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/send.png" alt="View" className="h-5 w-5" />
              <span>View All</span>
            </div>
          </a>
        ) : (
          <div className="flex items-center gap-2 rounded-full h-10 w-auto px-4 bg-gray-400 mt-4 text-white justify-center cursor-not-allowed">
            <span>Data Not Available!</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BattingStatsTable;
